import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { EtiquetadoContextService } from "../../context/etiquetado-context.service";
import { EtiquetadoService } from "../../services/etiquetado.service";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { Router } from "@angular/router";

@Component({
    selector: 'app-reetiquetado',
    templateUrl: './reetiquetado.component.html'
})
export class ReetiquetadoComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;
    private _filters;
    public filtersVisible = true;
    public filtersForm: UntypedFormGroup;

    public toCloseSpinner = 0;
    public exportModal = 0;

    public idColumn;
    public columnNumber: number;
    public orderBy = 'asc';
    public columnToSort: string; // Ordenación en front
    public columnToSortBack: string; // Ordenación en back

    public almacenes;
    public marcas;
    public denominaciones;
    public campanyas;
    public etiquetadores;

    public reetiquetado;
    public paisId;
    public denominacionId;

    public disabledAlmacenes: Subject<boolean> = new Subject();
    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledCampanyas: Subject<boolean> = new Subject();
    public disabledEtiquetadores: Subject<boolean> = new Subject();
    public disabledMarcas: Subject<boolean> = new Subject();

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public dropdownInfo: string;
    public dropdownSection: any;
    public pestanaActiva = new Array<number>();

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 1;
    public firstItemList = 0;
    public lastItemList = this.itemsPerPage;
    public totalItems: number;
    public readonly visiblePages = 3;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _spinnerService: SpinnerService,
        private readonly _sessionContext: SessionContextService,
        private readonly _etiquetadoService: EtiquetadoService,
        private readonly _etiquetadoContext: EtiquetadoContextService,
        private readonly _dataProvider: DataProvider,
        private readonly _utilsService: UtilsService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            pais: ['', Validators.required],
            denominacion: [{ value: '', disabled: true }, Validators.required],
            campanya: [{ value: '', disabled: true }, Validators.required],
            etiquetador: [{ value: '', disabled: true }],
            almacen: [{ value: '', disabled: true }],
            marca: [{ value: '', disabled: true }],
            paisDestino: [''],
            fechaInicio: [''],
            fechaFin: [''],
        });

        this._etiquetadoContext.reetiquetadoFilters = null;

        this.checkWarnings(this.filtersForm.get('pais').value);

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this.paisId = pais.id;
                this.getDenominacionesByPais(pais.id);
            } else {
                this.disabledDenominaciones.next(true);
                this.denominaciones = [];
                this.filtersForm.get('denominacion').patchValue('');
                this.filtersForm.get('denominacion').disable();
            }
        });

        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.filtersForm.get('pais').enabled) {
                this.filtersForm.get('pais').patchValue(this._dataProvider.paises[0]);
                this.filtersForm.get('pais').disable();
            }
        }

        this.filtersForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
            if (denominacion) {
                this.getCampanyasByDenominacion(denominacion.id, this.paisId);
            } else {
                this.disabledCampanyas.next(true);
                this.campanyas = [];
                this.filtersForm.get('campanya').patchValue('');
                this.filtersForm.get('campanya').disable();
            }
        });

        this.filtersForm.get('campanya').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((campanya) => {
            if (campanya) {
                this.getEtiquetadoresByCampanya(campanya.id);
            } else {
                this.disabledEtiquetadores.next(true);
                this.etiquetadores = [];
                this.filtersForm.get('etiquetador').patchValue('');
                this.filtersForm.get('etiquetador').disable();
            }
        });

        this.filtersForm.get('etiquetador').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((etiquetador) => {
            if (etiquetador) {
                this.getAlmacenesByEtiquetador(etiquetador.id);
                this.getMarcasByEtiquetador(etiquetador.id);
            } else {
                this.disabledAlmacenes.next(true);
                this.disabledMarcas.next(true);
                this.almacenes = [];
                this.marcas = [];
                this.filtersForm.get('almacen').patchValue('');
                this.filtersForm.get('almacen').disable();
                this.filtersForm.get('marca').disable();
                this.filtersForm.get('marca').patchValue('');
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get paises() {
        return this._dataProvider.paises;
    }

    public get destinos() {
        return this._dataProvider.destinos;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getDenominacionesByPais(paisId) {
        let filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getDenominaciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.denominaciones = resp.lista;
                if (this.denominaciones.length === 1) {
                    this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
                    this.filtersForm.get('denominacion').disable();
                    this.disabledDenominaciones.next(true);
                } else {
                    this.filtersForm.get('denominacion').enable();
                    this.disabledDenominaciones.next(false);
                }
            }
        });
    }

    public getCampanyasByDenominacion(denominacionId, paisId) {
        let filters = {
            denominacionId: denominacionId,
            paisId: paisId,
            volcadoHistorico: false
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getCampanyas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.campanyas = resp.lista;
                this.campanyas.forEach(campanya => {
                    campanya.des = campanya.anyo.toString();
                });
                this.filtersForm.get('campanya').enable();
                this.disabledCampanyas.next(false);
                this.filtersForm.get('campanya').patchValue(this.campanyas[0]);
            }
        });
    }

    public getAlmacenesByEtiquetador(etiquetadorId) {
        let filters = {
            etiquetadorId: etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getAlmacenesByEtiquetadorId(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.almacenes = resp.lista;
                this.filtersForm.get('almacen').enable();
                this.disabledAlmacenes.next(false);
            }
        });
    }

    public getEtiquetadoresByCampanya(campanyaId) {
        let filters = {
            campanyaId: campanyaId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getEtiquetadores(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.etiquetadores = resp.lista;

                if (this.etiquetadores.length === 1) {
                    this.filtersForm.get('etiquetador').patchValue(this.etiquetadores[0]);
                } else {
                    this.filtersForm.get('etiquetador').enable();
                    this.disabledEtiquetadores.next(false);
                }
            }
        });
    }

    public getMarcasByEtiquetador(etiquetadorId) {
        let filters = {
            paisId: this.paisId,
            etiquetadorId: etiquetadorId,
            denominacionId: this.denominacionId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getMarcas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.marcas = resp.lista;
                this.marcas.unshift({ id: -1, des: this._translate.instant('global.sinMarca') });
                this.filtersForm.get('marca').enable();
                this.disabledMarcas.next(false);
            }
        });
    }

    private checkWarnings(pais) {
        if (pais && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getReetiquetados();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
        }
    }

    public pageChange(page) {
        this.currentPage = page;
        this.firstItemList = this.itemsPerPage * (page - 1);
        this.lastItemList = this.itemsPerPage * page;
    }

    public itemsPerPageChange(items) {
        this.currentPage = 1;
        this.itemsPerPage = items;
        this.firstItemList = 0;
        this.lastItemList = items;
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public checkValuesForm(): boolean {
        return (
            (this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
            (this.filtersForm.get('denominacion').value && this.filtersForm.get('denominacion').enabled) ||
            this.filtersForm.get('campanya').value ||
            this.filtersForm.get('marca').value ||
            (this.filtersForm.get('etiquetador').value && this.filtersForm.get('etiquetador').enabled) ||
            this.filtersForm.get('almacen').value ||
            this.filtersForm.get('paisDestino').value ||
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
      if(this.filtersForm.get('pais').disabled) {
        this.filtersForm.reset({
          pais: this.filtersForm.get('pais').value
        });
      } else {
        this.filtersForm.controls['pais'].reset();
        this.filtersForm.controls['paisDestino'].reset();
        this.filtersForm.controls['fechaInicio'].reset();
        this.filtersForm.controls['fechaFin'].reset();
      }
      this.checkWarnings(this.filtersForm.get('pais').value);
    }

    public filtrar() {
        this.validDates();

        if (this.filtersForm.invalid) {
            if (Object.keys(this.filtersForm.controls).some(key => this.filtersForm.get(key).hasError('required'))) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
        } else {
            this.applyFilters();
        }
    }

    private applyFilters() {
        // Reseteamos los iconos de las flechas y la columna a ordenar en back
        this.columnNumber = 0;
        this.columnToSortBack = undefined;
        this.currentPage = 1;
        this._etiquetadoContext.reetiquetadoFilters = {
            campanyaId: this.filtersForm.get('campanya').value ? this.filtersForm.get('campanya').value.id : '',
            marcaId: this.filtersForm.get('marca').value ? this.filtersForm.get('marca').value.id : '',
            etiquetadorId: this.filtersForm.get('etiquetador').value ? this.filtersForm.get('etiquetador').value.id : '',
            almacenId: this.filtersForm.get('almacen').value ? this.filtersForm.get('almacen').value.id : '',
            paisDestinoId: this.filtersForm.get('paisDestino').value ? this.filtersForm.get('paisDestino').value.id : '',
            mostrarDesgloseConfeccion: true,
            desglosarPorAlmacen: true,
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
        };

        this.getReetiquetados();
    }

    public getReetiquetados() {
        if (this._etiquetadoContext.reetiquetadoFilters) {
            this._filters = this._etiquetadoContext.reetiquetadoFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._etiquetadoService.getReetiquetado(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.length;
            this.reetiquetado = resp;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public showSection(i) {
        this.dropdownSection = this.dropdownSection === i ? -1 : i;
        this.pestanaActiva[0] = this.pestanaActiva[0] > 0 ? this.pestanaActiva[0] : 1;
        // Desplazamiento a la izquierda para tablet (mismo tamaño máx. que en table.scss)
        if (typeof this.dropdownSection === 'string' && window.innerWidth <= 1199) {
            // Retardo en caso de que haya otro detalle abierto
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    public showConfeccion(i) {
        this.pestanaActiva[i] = 1;
    }

    public showDetails(i) {
        this.dropdownInfo = this.dropdownInfo === i ? -1 : i;
        if (typeof this.dropdownInfo === 'string' && window.innerWidth <= 1199) {
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    public getPercent(expedicion?, recepcionados?) {
        return expedicion && recepcionados ? ((expedicion / recepcionados) * 100).toFixed(2) : 0;
    }

    private sortColumns(column) {
        this.reetiquetado.forEach(el => {
            el.lsEtiquetadoAlmacenDto.forEach(elem => {
                elem.lsEtiquetadoCultivoDto.sort((a, b) => {
                    // Columnas con valores exclusivamente numéricos
                    if (this.columnToSort === 'sumaNumEtiquetasReimpresas' || this.columnToSort === 'sumaKgReimpresos' || this.columnToSort === 'sumaKgImpresosEfectivos')
                        switch (this.orderBy) {
                            case 'asc':
                                return a[column] - b[column];
                                break;
                            case 'desc':
                                return b[column] - a[column];
                                break;
                    } else {
                        switch (this.orderBy) {
                            case 'asc':
                                return a[column].localeCompare(b[column]);
                                break;
                            case 'desc':
                                return b[column].localeCompare(a[column]);
                                break;
                        }
                    }
                })
            })
        })
    }

    public toOrderBy(id: any, colNumber: number, column: string, columnBack: string) {
        this.idColumn = id;
        this.columnNumber = colNumber;
        this.columnToSort = column;
        this.columnToSortBack = columnBack;
        switch (this.orderBy) {
            case 'asc':
                this.orderBy = 'desc';
                break;
            case 'desc':
                this.orderBy = 'asc';
                break;
        }
        this.sortColumns(this.columnToSort);
    }

    public getIcon(id: any) {
        let iconClass = 'fa fa-sort';
        if (id === this.columnNumber) {
            iconClass = this.orderBy === 'desc' ? 'fa fa-sort-down' : 'fa fa-sort-up';
        }
        return iconClass;
    }

    private prepareReportForSorting() {
        // Si se ha ordenado alguna columna
        if (this.columnToSortBack) {
            this._filters.ordenAnidado = {
                orderItems: [
                    {
                        orderProperty: this.columnToSortBack,
                        orderByDescending: this.orderBy === 'desc' ? true : false
                    }
                ]
            }
        }
    }

    private downloadPdf() {
        this._etiquetadoService.getRepReetiquetadoPdf(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            const downloadLink = document.createElement('a');
            downloadLink.href = 'data:application/pdf;base64,' + resp.base64Archivo;
            downloadLink.download = resp.nombreArchivo;
            downloadLink.click();
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.file' });
        });
    }

    private downloadCsv() {
        this._etiquetadoService.getRepReetiquetadoCsv(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            const downloadLink = document.createElement('a');
            downloadLink.href = 'data:text/csv;base64,' + resp.base64Archivo;
            downloadLink.download = resp.nombreArchivo;
            downloadLink.click();
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.file' });
        });
    }

    public exportPdfOrCsv(type) {
        if (this.reetiquetado && this.reetiquetado.length > 0) {

            if (this._etiquetadoContext.reetiquetadoFilters) {
                this._filters = this._etiquetadoContext.reetiquetadoFilters;
                this._filters.tamPagina = 0;
                this._filters.tipoArchivo = this.exportModal === 1 ? 'Pdf' : 'Csv';
                this._filters.mostrarDesgloseConfeccion = type === 2;
                this.prepareReportForSorting();
            }
            
            this._spinnerService.showSpinner();
            this.exportModal === 1 ? this.downloadPdf() : this.downloadCsv();
        } else {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.exportNoData' });
        }

        this.exportModal = 0;
    }

    public openDocHelp() {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._etiquetadoService.getDocHelp('Labeling_Relabeling.pdf').toPromise().finally(() => {
          this.checkCloseSpinner();
      }).then((resp) => {
          localStorage.setItem('base64', resp.base64Archivo);
          const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
          window.open(url, '_blank');
      });
    }

}
